// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { shopByCategoryStyles } from "@rugby/App/Widgets/ShopByCategory/ShopByCategory.style";
import * as React from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const categoryPath = getSelectedCategoryPath(state) || (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath)?.value;
    const categories = getCategoriesState(catalogPageState, state);

    return {
        categories,
    };
};

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const styles = shopByCategoryStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const getCategoriesState = (catalogPageState: any, state: ApplicationState) => {
    let categories;
    if (catalogPageState) {
        const { properties: { relatedCategories } } = catalogPageState;
        if (relatedCategories) {
            const relatedCategoriesArr = JSON.parse(relatedCategories);
            if (relatedCategoriesArr.length) {
                categories = relatedCategoriesArr.map((id: string) => getCategoryState(state, id)?.value);
                categories = categories.filter((a: {}) => a);
            }
        }
    }
    return categories;
}

const RelatedCategories: React.FC<Props> = ({ categories, fields }: Props) => {
    if (!categories || categories?.length === 0) {
        return null;
    }

    return (
        <StyledWrapper data-test-selector="relatedCategories">
            {fields.title && <Typography {...styles.TitleText}>{fields.title}</Typography>}
            <GridContainer {...styles.container} gap={30} data-test-selector="related_categories_list">
                {categories?.map((category: any) => (
                    <GridItem {...styles.categoryGridItem} key={`${category.id}`}>
                        <Link {...styles.categoryLink} href={category.path}>
                            <LazyImage
                                {...styles.categoryImage}
                                src={category.smallImagePath}
                                altText={category.imageAltText || category.shortDescription}
                            />
                            <StyledWrapper {...styles.categoryTextWrapper}>
                                <Typography {...styles.categoryText}>{category.shortDescription}</Typography>
                            </StyledWrapper>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(RelatedCategories),
    definition: {
        group: "Categories",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Related Categories",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
